import { useState, useEffect } from 'react'
import { Clock } from 'lucide-react'
import api from '@/services/api'
import { formatDate } from '@/utils/format-date'
import type { EmpresaDetailed } from '@/types'

interface HistoryItem {
  id: string
  action: string
  details?: string
  createdAt: string
  user?: { name: string }
}

interface EmpresaHistoryTabProps {
  empresa: EmpresaDetailed
}

export function EmpresaHistoryTab({ empresa }: EmpresaHistoryTabProps) {
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    const fetchHistory = async () => {
      try {
        const resp = await api.get(`/empresas/${empresa.id}/history`, { signal: controller.signal })
        setHistory(resp.data.data ?? [])
      } catch {
        setHistory([])
      } finally {
        setLoading(false)
      }
    }
    fetchHistory()
    return () => controller.abort()
  }, [empresa.id])

  if (loading) {
    return (
      <div className='p-4 text-center'>
        <p className='text-[var(--font-color-muted)] text-sm'>Carregando histórico...</p>
      </div>
    )
  }

  if (history.length === 0) {
    return (
      <div className='p-8 text-center'>
        <Clock size={48} className='mx-auto mb-3 text-[var(--text-muted)] opacity-50' />
        <p className='text-[var(--font-color-body)] font-medium'>Nenhum histórico encontrado</p>
        <p className='text-[var(--font-color-muted)] text-sm mt-1'>
          Atividades relacionadas a esta empresa aparecerão aqui
        </p>
      </div>
    )
  }

  return (
    <div className='space-y-2'>
      {history.map((item) => (
        <div
          key={item.id}
          className='flex items-start gap-3 p-3 rounded-lg hover:bg-[var(--glass-bg)] transition-colors'
        >
          <div className='w-2 h-2 rounded-full bg-[var(--brand-primary)] mt-2 shrink-0' />
          <div className='flex-1 min-w-0'>
            <p
              className='font-medium text-[var(--font-color-body)]'
              style={{ fontSize: 'var(--font-size-sm)' }}
            >
              {item.action}
            </p>
            {item.details && (
              <p
                className='text-[var(--font-color-muted)] mt-0.5'
                style={{ fontSize: 'var(--font-size-xs)' }}
              >
                {item.details}
              </p>
            )}
            <p
              className='text-[var(--font-color-muted)] mt-1'
              style={{ fontSize: 'var(--font-size-xs)' }}
            >
              {item.user?.name && `${item.user.name} · `}{formatDate(item.createdAt)}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}
